import _ from "lodash";
import PropTypes from "prop-types";
import React, { createRef } from "react";
import { connect } from "react-redux";
import classes from "./DropzoneComponent.scss";
import Dropzone from "react-dropzone";
import { fromEvent } from "file-selector";
import classNames from "classnames";
import {
  uploadFile,
  uploadFileV2,
  uploadFilesV3,
  removeProgressElement,
  heicToJpg,
  exceededSizeError,
  validateResourceLabels,
  updateResourceAttachments,
  unsupportedFileExtensionError,
} from "modules/Services";
import { removeFiles } from "OrganizationResources/modules/OrganizationResourcesModule/OrganizationResourcesThunks";
import {
  getAttachmentSendObj,
  generateRandomId,
  getMaxSize,
  getFileTypesAndMaxSizes,
  getFileTypesAndMaxSizesV2,
  buildStructure,
  getUpdatedMaxSize,
  getFileExtension,
  getMaxSizeForVideoFiles,
  getMaxSizeForZipFiles,
} from "Utils";
import LockedScreenWrapper from "UIComponents/LockedScreenComponents";
import I18nHOC from "UIComponents/I18nHOC";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getCurriculumProgramFreeStatus } from "Platform/modules/PlatformModule";
import ACLStore from "lib/aclStore";
import { isWindows } from "Utils/DeviceDetectHelpers";
import { RejectedFilesModal, ConflictingLabelModal } from "./components";
import { compose } from "lib/apolloClient";
import {
  getFileExtensionFromFilename,
  getMimeTypeFromFileName,
  ARCHIVE_MIME_TYPES,
  getFileExtensionFromMimeType,
} from "Utils/AttachmentHelpers";
import { BLOCKED_FILE_EXTENSIONS, BLOCKED_FILE_MIME_TYPES } from "Constants";
import { Button, Modal } from "@toddle-edu/ds-web";
import { is500MBVideoUploadLimitEnabled } from "Post/utils";

const DIALOGS_INFO = {
  /**
   *  This component cannot be localized as it is
   *  external and it's default prop is dialogueBoxInfo
   */

  CONFIRM: {
    title: "Bulk Upload File",
    message: () => (
      <div style={{ marginBottom: "16px", color: "red" }}>
        {`Are you sure you want to upload? This action can't be undone.`}
      </div>
    ),
    confirmButtonText: "Confirm",
    cancelButtonText: "Cancel",
  },
};
DIALOGS_INFO.CONFIRM.message.displayName = "CONFIRM_MESSAGE";

const FILE_CHUNK_SIZE = 50;

class DropzoneComponent extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      showLockScreen: false,
      showConfirmationDialogue: false,
      currentDialogAction: "",
      jsonResourceObj: {},
      indexMapping: {},
      conflictData: {},
      rejectedFiles: {},
      fileMapping: {},
      isDragActive: false,
      viewportHeight: window.innerHeight,
      isEndofPage: false,
    };
    this.dropZoneRef = {};
    this.dummyContainerRef = createRef(null);
    this.DIALOGS_INFO_V2 = {
      CONFLICTING_LABELS: {
        onConfirmClick: this.handleConflictingNames,
      },
      REJECTED_FILES: {
        onConfirmClick: this.removeRejectedFiles,
      },
    };
  }

  componentDidMount() {
    window.addEventListener("scroll", this.handleScroll, true);
    window.addEventListener("resize", this.handleResize);
    this.handleIsEndOfthePage();
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("scroll", this.handleScroll, true);
  }

  componentDidUpdate(prevProps) {
    const { parentId, parentType } = this.props;
    const { parentId: prevParentId, parentType: prevParentType } = prevProps;
    const { isEndofPage } = this.state;
    if (
      parentId !== prevParentId ||
      parentType !== prevParentType ||
      isEndofPage
    ) {
      this.handleIsEndOfthePage();
    }
  }

  getDummyElementRefData = dummyContainerRef => {
    return dummyContainerRef?.current?.getBoundingClientRect();
  };

  handleResize = () => {
    this.setState({ viewportHeight: window.innerHeight });
  };

  isDummyElementAtEndOfthePage = dummyContainerRef => {
    const dummyElementRefData = this.getDummyElementRefData(dummyContainerRef);
    const windowHeight =
      window.innerHeight || document.documentElement.clientHeight;
    if (
      dummyElementRefData?.top > 0 &&
      dummyElementRefData?.bottom < windowHeight &&
      dummyElementRefData?.bottom !== 0
    ) {
      return true;
    }
    return false;
  };

  handleIsEndOfthePage = () => {
    if (this.isDummyElementAtEndOfthePage(this.dummyContainerRef)) {
      this.setState({ isEndofPage: true });
    } else {
      this.setState({ isEndofPage: false });
    }
  };

  handleScroll = () => {
    this.handleIsEndOfthePage();
  };

  customizedFile = async file => {
    let customFile;
    if (this.props.customizedFile) {
      customFile = await this.props.customizedFile(file);
    } else {
      return file;
    }
    return customFile;
  };
  customizedFileObject = async (file, item) => {
    let sendObj;
    if (this.props.customizedFileObject) {
      sendObj = await this.props.customizedFileObject(file, item);
    } else {
      sendObj = await getAttachmentSendObj(file, item);
    }
    return sendObj;
  };

  filterRestrictedFiles = files => {
    const {
      excludeFileExtensionsList = [],
      unsupportedFileExtensionError,
    } = this.props;
    const blockMaliciousMimeTypes = ACLStore.can(
      "FeatureFlag:BlockMaliciousMimeType"
    );
    const blockedFileExtensions = blockMaliciousMimeTypes
      ? _.union(excludeFileExtensionsList, BLOCKED_FILE_EXTENSIONS)
      : excludeFileExtensionsList;
    const blockedFileMimeTypes = blockMaliciousMimeTypes
      ? BLOCKED_FILE_MIME_TYPES
      : [];
    const notAllowedFileExtensions = [];
    const filteredFiles = _.filter(files, file => {
      const mimeType =
        file?.type || getMimeTypeFromFileName({ fileName: file?.name });

      const extension = `.${
        getFileExtensionFromFilename({
          fileName: file?.name,
        }) || getFileExtensionFromMimeType({ mimeType })
      }`;

      if (
        _.includes(blockedFileExtensions, extension) ||
        _.includes(blockedFileMimeTypes, mimeType)
      ) {
        notAllowedFileExtensions.push(extension);
        return false;
      }
      return true;
    });

    if (_.size(notAllowedFileExtensions) > 0) {
      unsupportedFileExtensionError({
        notAllowedFileExtensions: _.uniq(notAllowedFileExtensions),
        notAllowedFileCount: _.size(notAllowedFileExtensions),
      });
    }
    return filteredFiles;
  };

  onDropRejected = e => {
    const {
      isCurriculumProgramFree,
      accept,
      setToastMsg,
      disableDragNDrop,
    } = this.props;

    const { isDragActive } = this.state;

    // allow the click but not the drag and drop
    if (disableDragNDrop && isDragActive) return;

    console.error("REJECTED", e);

    if (isCurriculumProgramFree) {
      this.setState({ showLockScreen: true });
    } else {
      // If accept array is empty, all file types are supported
      const unsupportedFiles = _.isEmpty(accept)
        ? [] // If accept is empty, return an empty array as no filtering is needed
        : _.reduce(
            e,
            (files, item) => {
              if (!_.includes(accept, item?.type)) {
                return [...files, item?.name];
              }
              return files;
            },
            []
          );

      if (!_.isEmpty(unsupportedFiles)) {
        setToastMsg({
          msg: "toastMsgs:file_type_not_supported_with_file_names",
          locale_params: [
            {
              key: "files",
              value: _.join(unsupportedFiles, ", "),
              isPlainText: true,
            },
          ],
          type: "warning",
          position: "toast-top-center",
        });
      }

      if (
        _.some(
          e,
          file =>
            file?.type === "" &&
            _.includes(["heif", "heic"], getFileExtension(file?.name))
        )
      ) {
        setToastMsg({
          msg: "toastMsgs:file_type_not_supported_in_device",
          locale_params: [
            {
              key: "files",
              value: _.join(unsupportedFiles, ", "),
              isPlainText: true,
            },
          ],
          type: "warning",
          position: "toast-top-center",
        });
      } else if (_.size(e) > _.size(unsupportedFiles)) {
        if (this.props.isFolderPicker) {
          const fileTypesAndMaxSizes = getFileTypesAndMaxSizesV2({
            files: e,
            allowedFiles: unsupportedFiles,
            maxSize: this.props.maxSize,
            areFilesInRawFormat: true,
          });
          this.setState({
            rejectedFiles: fileTypesAndMaxSizes,
          });
        } else {
          const fileTypesAndMaxSizes = getFileTypesAndMaxSizes({
            files: e,
            allowedFiles: unsupportedFiles,
            fileTypePropName: "type",
            maxSize: this.props.maxSize,
            maxSizeForVideoFiles: this.props.maxSizeForVideoFiles,
            maxSizeForZipFiles: this.props.maxSizeForZipFiles,
            is500MBVideoLimitEnabled: is500MBVideoUploadLimitEnabled(),
            isIncreasedUploadLimit: this.props.isIncreasedUploadLimit,
          });
          this.props.exceededSizeError({
            fileTypesAndMaxSizes,
          });
        }
      }
    }
  };

  onDrop = async files => {
    const that = this;
    const {
      parentType,
      parentId,
      shouldUpload,
      isAuthenticated,
      portalType,
      uploadType,
      exceededSizeError,
      isMaxSizeArray,
      sendFileOnContentUpload = false,
      excludeFileExtensionsList = [],
      unsupportedFileExtensionError,
      customFilterFilesHandler,
      onContentUploaded,
      updateIsUploading,
      isIncreasedUploadLimit = false,
      maxSize,
      maxSizeForVideoFiles,
      maxSizeForZipFiles,
    } = that.props;

    let filterFiles = files;

    // filter files based on custom logic
    if (customFilterFilesHandler) {
      filterFiles = customFilterFilesHandler(filterFiles);
    }

    // if files are rejected on the basis of mimeType then onDropRejected function will be called.
    // filtering files on basis of extension from excludedFileExtensionsList and BLOCKED_FILE_EXTENSIONS
    filterFiles = this.filterRestrictedFiles(filterFiles);

    if (!isMaxSizeArray) {
      const oversizedFiles = _.filter(filterFiles, file => {
        let maxSizeForFile = maxSize;

        if (
          (is500MBVideoUploadLimitEnabled() &&
            _.startsWith(_.get(file, "type", ""), "video/")) ||
          _.includes(
            getMimeTypeFromFileName({ fileName: _.get(file, "name", "") }),
            "video"
          )
        ) {
          maxSizeForFile = maxSizeForVideoFiles;
        }

        if (
          isIncreasedUploadLimit &&
          _.includes(
            ARCHIVE_MIME_TYPES,
            file?.type ||
              getMimeTypeFromFileName({ fileName: _.get(file, "name", "") })
          )
        ) {
          maxSizeForFile = maxSizeForZipFiles;
        }

        // Return files that exceed their respective size limits
        return maxSizeForFile < _.get(file, "size", 0);
      });

      if (!_.isEmpty(oversizedFiles)) {
        this.onDropRejected(oversizedFiles);
        filterFiles = _.filter(filterFiles, file => {
          return !_.some(
            oversizedFiles,
            oversizedFile =>
              file.name === oversizedFile.name &&
              file.size === oversizedFile.size &&
              file.lastModified === oversizedFile.lastModified
          );
        });
      }
    }

    // Filtering files on basis of maxSizes
    if (isMaxSizeArray) {
      const supportedFiles = _.filter(filterFiles, file => {
        const updatedMaxSize = getUpdatedMaxSize({
          maxSize: this.props.maxSize,
          fileType: file?.type,
        });

        return updatedMaxSize >= _.get(file, "size", 0);
      });

      if (_.size(filterFiles) > _.size(supportedFiles)) {
        const fileTypesAndMaxSizes = getFileTypesAndMaxSizes({
          files: filterFiles,
          allowedFiles: supportedFiles,
          fileTypePropName: "type",
          maxSize: this.props.maxSize,
        });

        exceededSizeError({
          fileTypesAndMaxSizes,
        });
      }
      filterFiles = supportedFiles;
    }

    if (_.isEmpty(filterFiles)) {
      return;
    }
    const allPromises = _.map(filterFiles, async file => {
      const uploadId = generateRandomId();
      const fileType = _.get(file, "type", "");
      if (this.props.updateFilesCount) {
        this.props.updateFilesCount(1);
      }

      // heic to jpg conversion
      if (ACLStore.can("FeatureFlag:EnableHEICConversion")) {
        file = await this.props.heicToJpg({
          file,
          uploadId,
          startProgress: 0,
          parentType,
          parentId,
        });
      }

      file = await this.customizedFile(file);
      let sendObj = file
        ? await this.customizedFileObject(file, `${file.preview}`)
        : null;
      if (this.props.onDropContent) {
        this.props.onDropContent({ ...sendObj, file: file, id: uploadId });
      }

      if (_.get(file, "type", "") !== fileType) {
        _.set(sendObj, "metadata.originalFileType", fileType);
      }
      try {
        if (file && shouldUpload) {
          const uploadStartTime = Date.now();
          const item = await this.props.uploadFileV2({
            file,
            parentType,
            parentId,
            attachment: sendObj,
            isAuthenticated,
            uploadId,
            portalType,
            expiry: this.props.expiry,
            shouldReturnSignedUrl: this.props.shouldReturnSignedUrl,
          });

          const uploadTime = Date.now() - uploadStartTime;
          const metadata = _.get(sendObj, "metadata", {});
          const updatedMetadata = { ...metadata, uploadTime };
          sendObj = { ...sendObj, metadata: updatedMetadata, url: item };
          if (this.props.onSingleContentUploaded) {
            this.props.onSingleContentUploaded(sendObj, uploadId, {
              parentId,
              parentType,
              uploadType,
            });
          }
          if (this.props.updateFilesCount) {
            this.props.updateFilesCount(-1);
          }
        } else {
          if (this.props.updateFilesCount) {
            this.props.updateFilesCount(-1);
          }
        }
        return sendFileOnContentUpload
          ? { ...sendObj, id: uploadId, file: file }
          : sendObj;
      } catch (e) {
        if (this.props.updateFilesCount) {
          this.props.updateFilesCount(-1);
        }
        return null;
      }
    });

    if (updateIsUploading) {
      updateIsUploading(true);
    }
    const values = _.filter(await Promise.all(allPromises), urlObj => urlObj);
    if (onContentUploaded) {
      onContentUploaded(values); // Sends all files' sendObj together as an array
    }
    if (updateIsUploading) {
      updateIsUploading(false);
    }

    /**
 * @description : Old Uploading Code (Commented by Chaitu)

 */

    // let allPromises = files.map((file, key) => {
    //   if (that.props.updateFilesCount) {
    //     that.props.updateFilesCount(1);
    //   }
    //   return new Promise(async resolve => {
    //     let sendObj = { file };
    //     if (that.customizedFileObject) {
    //       sendObj = await that.customizedFileObject(file, `${file.preview}`);
    //     }
    //     let uploadObj = {};

    //     if (shouldUpload) {
    //       uploadObj = that.props.uploadFile({
    //         file,
    //         parentType,
    //         parentId,
    //         attachment: sendObj,
    //         isAuthenticated
    //       });
    //     }
    //     const { promise, uploadId } = uploadObj;
    //     if (that.props.onDropContent) {
    //       that.props.onDropContent({ ...sendObj, file: file, id: uploadId });
    //     }

    //     console.log("uploadObj", uploadObj, shouldUpload);

    //     if (shouldUpload) {
    //       promise
    //         .then(async item => {
    //           let sendObj = await that.customizedFileObject(file, item);

    //           if (that.props.onSingleContentUploaded) {
    //             await that.props.onSingleContentUploaded(sendObj, uploadId);
    //           }
    //           if (that.props.updateFilesCount) {
    //             that.props.updateFilesCount(-1);
    //           }
    //           console.log(sendObj, "sendObj");
    //           that.props.removeProgressElement({ id: uploadId });
    //           resolve(sendObj);
    //         })
    //         .catch(e => {
    //           console.log(e, "dropzone error");
    //           if (that.props.updateFilesCount) {
    //             that.props.updateFilesCount(-1, true);
    //           }
    //         });
    //     } else {
    //       if (that.props.updateFilesCount) {
    //         that.props.updateFilesCount(-1);
    //       }
    //       console.log(sendObj, "sendObj");
    //       resolve(sendObj);
    //     }
    //   });
    // });
    // console.log(allPromises, "allPromises");
    // Promise.all(allPromises)
    //   .then(values => {
    //     console.log(values, "sendObj");
    //     if (this.props.onContentUploaded) {
    //       this.props.onContentUploaded(values);
    //     }
    //   })
  };

  resetLocalState = () => {
    // reset all the states
    this.setState({
      jsonResourceObj: {},
      indexMapping: {},
      conflictData: {},
      rejectedFiles: {},
      fileMapping: {},
    });
  };

  onDropV2 = async files => {
    this.setState({
      isLoading: true,
    });
    const { shouldAllowConflictingLabels, parentType, parentId } = this.props;

    // filtering files on basis of extension from excludedFileExtensionsList and BLOCKED_FILE_EXTENSIONS
    files = this.filterRestrictedFiles(files);

    const customizedFiles = _.map(
      _.filter(files, file => !_.startsWith(file.name, ".")),
      async file => {
        const fileType = _.get(file, "type", "");
        if (ACLStore.can("FeatureFlag:EnableHEICConversion")) {
          file = await this.props.heicToJpg({
            file,
            uploadId: generateRandomId(),
            startProgress: 0,
            parentType,
            parentId,
            hideToastProgress: true,
          });
        }
        file = await this.customizedFile(file);
        const sendObj = file
          ? await this.customizedFileObject(file, `${file.preview}`)
          : null;

        if (_.get(file, "type", "") !== fileType) {
          _.set(sendObj, "metadata.originalFileType", fileType);
        }

        return { ...sendObj, file };
      }
    );

    const { jsonResourceObj, fileMapping } = buildStructure(
      await Promise.all(customizedFiles)
    );
    await new Promise(resolve =>
      this.setState({ jsonResourceObj, fileMapping }, resolve)
    );

    if (!shouldAllowConflictingLabels) {
      // index and resourceID mapping which will be useful to optimally update the JSON
      const indexMapping = {};

      const rootResources = _.map(
        _.filter(jsonResourceObj, (resource, index) => {
          indexMapping[resource.resourceMapId] = index;
          if (resource.parentResourceMapId === null) {
            return true;
          }
          return false;
        }),
        resource => {
          const { label, resourceType, resourceMapId, attachment } = resource;
          const mimeType = _.get(attachment, "mimeType");
          const sendObj = {
            label,
            resourceType,
            resourceMapId,
          };
          if (resourceType === "FOLDER") {
            return sendObj;
          }
          return {
            ...sendObj,
            mimeType,
          };
        }
      );

      this.setState({ indexMapping });

      const doesConflictingResourcesExist = await this.validateResourceLabels({
        rootResources,
      });

      if (doesConflictingResourcesExist) {
        this.setState({
          isLoading: false,
          currentDialogAction: "CONFLICTING_LABELS",
        });
      } else {
        this.validateFiles();
      }
    } else {
      this.validateFiles();
    }
  };

  validateResourceLabels = async ({ rootResources }) => {
    const { parentId, parentType, isRootDirectory, visibleTo } = this.props;
    const { jsonResourceObj, indexMapping } = this.state;

    const {
      resources: conflictingResources = [],
      conflicts = {},
    } = await this.props.validateResourceLabels({
      parentId,
      type: parentType,
      isRootDirectory,
      resources: rootResources,
      visibleTo,
    });

    const doesConflictingResourcesExist = !!conflicts?.invalidLabels;
    if (doesConflictingResourcesExist) {
      this.setState({
        conflictData: {
          resources: _.map(
            _.filter(
              conflictingResources,
              resource => resource.doesResourceExist
            ),
            resource => ({
              ...jsonResourceObj[indexMapping[resource.resourceMapId]],
              suggestedLabel: resource.suggestedLabel,
            })
          ),
          metadata: conflicts,
        },
      });
    }

    return doesConflictingResourcesExist;
  };

  // accept/reject acc to file type and file size
  validateFiles = async () => {
    const { jsonResourceObj, rejectedFiles } = this.state;
    const {
      exceededSizeError,
      isMaxSizeArray,
      isFileNameValidation,
      checkFileNameValidation,
      maxSize,
    } = this.props;

    if (isMaxSizeArray) {
      const files = _.map(
        _.filter(jsonResourceObj, resource => resource.resourceType === "FILE"),
        file => {
          return {
            ...file.attachment,
            resourceMapId: file.resourceMapId,
          };
        }
      );

      let filterFiles = files;

      filterFiles = _.filter(files, file => {
        const updatedMaxSize = getUpdatedMaxSize({
          maxSize,
          fileType: file?.mimeType,
        });

        return updatedMaxSize >= _.get(file, "metadata.size", 0);
      });

      if (_.size(files) > _.size(filterFiles)) {
        const fileTypesAndMaxSizes = getFileTypesAndMaxSizesV2({
          files,
          allowedFiles: filterFiles,
          maxSize,
        });

        this.setState({
          isLoading: false,
          rejectedFiles: fileTypesAndMaxSizes,
          currentDialogAction: "REJECTED_FILES",
        });
      } else {
        // continue with the upload part
        this.uploadFilesInChunks();
      }
    } else if (isFileNameValidation) {
      const {
        updatedJsonResourceObj,
        updatedRejectedFile,
        isError,
      } = await checkFileNameValidation({
        jsonResourceObj,
        rejectedFiles,
        maxSize,
      });
      if (isError) {
        this.resetLocalState();
        return;
      } else if (!_.isEmpty(updatedRejectedFile)) {
        await new Promise(resolve =>
          this.setState(
            {
              isLoading: false,
              jsonResourceObj: updatedJsonResourceObj,
              rejectedFiles: updatedRejectedFile,
              currentDialogAction: "REJECTED_FILES",
            },
            resolve
          )
        );
      } else {
        await new Promise(resolve =>
          this.setState({ jsonResourceObj: updatedJsonResourceObj }, resolve)
        );
        // continue with the upload part
        this.uploadFilesInChunks();
      }
    } else if (!_.isEmpty(rejectedFiles)) {
      this.setState({
        isLoading: false,
        currentDialogAction: "REJECTED_FILES",
      });
    } else {
      // continue with the upload part
      this.uploadFilesInChunks();
    }
  };

  handleConflictingNames = selectedOption => {
    this.toggleDialogueBoxV2Display();

    switch (selectedOption) {
      case "replaceExisting":
        // continue the process and store the conflicting node IDs
        this.validateFiles();
        break;
      case "keepBoth":
        // need to update the label and discard the conflicting ids
        this.setState(prevState => {
          return {
            conflictData: {
              ...prevState.conflictData,
              metadata: {
                ...prevState.conflictData?.metadata,
                ids: [],
              },
            },
          };
        });
        this.updateFileLabel();
        break;
    }
  };

  updateFileLabel = () => {
    const {
      jsonResourceObj,
      indexMapping,
      conflictData: { resources },
    } = this.state;

    const updatedJSONResourceObj = _.cloneDeep(jsonResourceObj);

    _.forEach(resources, resource => {
      const indexToModify = indexMapping[resource.resourceMapId];
      const updatedJSONResource = updatedJSONResourceObj[indexToModify];
      if (updatedJSONResource) {
        updatedJSONResource.label = resource.suggestedLabel;
        if (updatedJSONResource.resourceType === "FILE") {
          updatedJSONResource.attachment.name = resource.suggestedLabel;
        }
      }
    });
    this.setState({ jsonResourceObj: updatedJSONResourceObj }, () => {
      // proceed further
      this.validateFiles();
    });
  };

  removeRejectedFiles = () => {
    this.toggleDialogueBoxV2Display();

    const { jsonResourceObj, rejectedFiles, indexMapping } = this.state;
    const updatedJSONResourceObj = _.cloneDeep(jsonResourceObj);

    // if maxSizeArray is false - rejected files are taken care by onDropRejected
    if (this.props.isMaxSizeArray) {
      _.map(rejectedFiles, file => {
        const indexToRemove = indexMapping[file.resourceMapId];

        updatedJSONResourceObj.splice(indexToRemove, 1);
      });
    }
    this.setState(
      {
        jsonResourceObj: updatedJSONResourceObj,
        indexMapping: null,
      },
      () => {
        // send json to BE
        // start uploading files in batches
        this.uploadFilesInChunks();
      }
    );
  };

  uploadFilesInChunks = async () => {
    const {
      props: {
        parentType,
        parentId,
        isAuthenticated,
        portalType,
        onCreateResourceMapping,
        onContentUploaded,
        setToastMsg,
      },
      state: {
        jsonResourceObj,
        conflictData: {
          metadata: { ids: conflictingResourceIds } = { ids: [] },
        } = {},
        fileMapping,
      },
    } = this;

    if (_.isEmpty(jsonResourceObj)) {
      this.setState({
        isLoading: false,
      });
      this.resetLocalState();
      return;
    }

    this.setState({
      isLoading: true,
    });

    const files = _.filter(jsonResourceObj, { resourceType: "FILE" });
    const indexMapping = _.reduce(
      files,
      (mapping, file, index) => {
        mapping[file.resourceMapId] = index;
        return mapping;
      },
      {}
    );

    //Note: we are omitting url from the attachment object as BE currently accepts CDN urls and in our case it was blob url.
    const jsonResourceObjWithoutBlobUrl = _.map(jsonResourceObj, item => {
      const attachment = _.get(item, "attachment");

      if (!attachment) {
        return item;
      }

      const url = _.get(attachment, "url");
      const isBlobUrl = _.startsWith(url, "blob:");

      if (!isBlobUrl) {
        return item;
      }

      return {
        ...item,
        attachment: _.omit(attachment, ["url"]),
      };
    });

    const folderStructure = await onCreateResourceMapping({
      organizationResources: jsonResourceObjWithoutBlobUrl,
      conflictingResourceIds,
    });

    this.setState({
      isLoading: false,
    });

    if (!folderStructure) {
      this.resetLocalState();
      return;
    }

    // just iterate through the response and attach the id and attachment ID
    _.filter(
      folderStructure,
      resource => resource.resourceType === "FILE"
    ).forEach(file => {
      const indexToModify = indexMapping[file.metadata.resourceMapId];

      if (files[indexToModify]) {
        files[indexToModify].id = file.id;
        files[indexToModify].attachment.id = file.attachment.id;
      }
    });

    const chunkedFiles = _.chunk(files, FILE_CHUNK_SIZE);

    const myPromises = _.map(chunkedFiles, async chunk => {
      try {
        const { signedUrls, cancelledFiles } = await this.props.uploadFilesV3({
          parentType,
          parentId,
          attachment: _.map(chunk, file => {
            return {
              ...file.attachment,
              resourceMapId: file.resourceMapId,
              fileId: file.id,
            };
          }),
          portalType,
          isAuthenticated,
          fileMapping,
        });

        const sendObj = _.map(
          signedUrls,
          ({ url, resourceMapId, uploadTime }) => {
            const index = indexMapping[resourceMapId];
            const file = files[index];

            return {
              id: file.id,
              attachment: {
                id: file.attachment.id,
                url,
                metadata: {
                  ...file.attachment.metadata,
                  uploadTime,
                },
              },
            };
          }
        );

        // run delete file mutation to delete the resp
        if (!_.isEmpty(cancelledFiles)) {
          await this.props.removeFiles(cancelledFiles);
        }

        // if sendObj is not [] then call a mutation which will attach the urls to the respective attachments
        if (!_.isEmpty(sendObj)) {
          await this.props.updateResourceAttachments(sendObj);
        }

        return sendObj;
      } catch (e) {
        return null;
      }
    });

    const allFileUrls = _.filter(
      await Promise.all(myPromises),
      urlObj => urlObj
    );

    // when all the process is done

    // show a successfully added toast message
    setToastMsg({
      msg: "toastMsgs:successfully_with_label",
      locale_params: [
        {
          key: "label",
          value: "toastMsgs:added",
          isPlainText: false,
        },
      ],
      type: "success",
      position: "toast-bottom-left",
    });

    if (onContentUploaded) {
      onContentUploaded(allFileUrls);
    }

    this.resetLocalState();
  };

  onClose = () => {
    if (this.props.onClose) {
      this.props.onClose();
    }
  };

  onClick = () => {
    if (this.props.onClick) {
      this.props.onClick();
    }
  };

  onCloseLockScreenModal = () => {
    this.setState({ showLockScreen: false });
  };

  toggleDialogueBoxDisplay = () =>
    this.setState({
      showConfirmationDialogue: !this.state.showConfirmationDialogue,
    });

  toggleDialogueBoxV2Display = () =>
    this.setState({
      currentDialogAction: "",
    });

  dialogueBoxConfirm = () => {
    this.dropZoneRef.open();
  };

  dialogueBoxV2Confirm = dialogueConfig => params => {
    dialogueConfig?.onConfirmClick(params);
  };

  renderModalComponent = () => {
    const { currentDialogAction } = this.state;
    const {
      t,
      isMaxSizeArray,
      maxSize,
      isFileNameValidation,
      testId,
    } = this.props;
    const dialogueBoxConfig = this.DIALOGS_INFO_V2?.[currentDialogAction];

    const handleButton1Click = () => {
      this.toggleDialogueBoxV2Display();
      this.resetLocalState();
    };

    switch (currentDialogAction) {
      case "REJECTED_FILES":
        return (
          <RejectedFilesModal
            handleButton1Click={handleButton1Click}
            handleButton2Click={this.dialogueBoxV2Confirm(dialogueBoxConfig)}
            rejectedFiles={this.state.rejectedFiles}
            maxSize={isMaxSizeArray ? undefined : maxSize}
            isFileNameValidation={isFileNameValidation}
            testId={`${testId}-rejectedFilesModal`}
            t={t}
          />
        );
      case "CONFLICTING_LABELS":
        return (
          <ConflictingLabelModal
            handleButton1Click={handleButton1Click}
            handleButton2Click={this.dialogueBoxV2Confirm(dialogueBoxConfig)}
            conflictingResources={this.state.conflictData?.resources}
            t={t}
            testId={`${testId}-conflictingLabelModal`}
          />
        );
      default:
        return null;
    }
  };

  getUploadTextBottomPosition = () => {
    const { viewportHeight, isEndofPage } = this.state;
    const dummyElementRefData = this.getDummyElementRefData(
      this.dummyContainerRef
    );

    if (isEndofPage && dummyElementRefData?.bottom > 0) {
      return {
        top: dummyElementRefData?.bottom - 80,
      };
    }
    return {
      top: viewportHeight - 80,
    };
  };

  handleOnDragEnter = () => {
    this.setState({ isDragActive: true });
  };

  handleOnDragLeave = () => {
    this.setState({ isDragActive: false });
  };

  handleOnDrop = files => {
    const { disableDragNDrop } = this.props;
    const { isDragActive } = this.state;

    if (disableDragNDrop && isDragActive) return;

    this.setState({ isDragActive: false });
    this.props.isFolderPicker ? this.onDropV2(files) : this.onDrop(files);
  };

  renderDragNDropAnimation = () => {
    const { isDragActive } = this.state;
    const { t, shouldShowDragNDropOverlay } = this.props;

    const dragBackgroundColorClass = classNames({
      [classes.dragBackgroundColor]: isDragActive,
    });

    return shouldShowDragNDropOverlay ? (
      <>
        {isDragActive ? (
          <div
            className={classes.onDropText}
            style={this.getUploadTextBottomPosition()}
          >
            <div className="text-body-1">
              {t("common:drop_files_folders_to_upload")}
            </div>
          </div>
        ) : null}
        <div className={dragBackgroundColorClass} />
      </>
    ) : null;
  };

  getDropzoneInputProps = () => {
    const { isFolderPicker } = this.props;

    return isFolderPicker
      ? {
          directory: "", // This allows selecting directories
          webkitdirectory: "", // This allows selecting directories in Chrome
          mozdirectory: "", // This allows selecting directories in Firefox
          tabIndex: "-1",
          "aria-hidden": "true",
        }
      : {
          tabIndex: "-1",
          "aria-hidden": "true",
        };
  };

  render() {
    const {
      showLockScreen,
      showConfirmationDialogue,
      isLoading,
      currentDialogAction,
    } = this.state;

    const {
      isDialogueBoxEnabled,
      dialogueBoxInfo,
      disableClick,
      dropzoneClass,
      maxSize,
      maxSizeForVideoFiles,
      maxSizeForZipFiles,
      isMaxSizeArray,
      isFolderPicker,
      multiple,
      dropZoneStyle,
      dropzoneContainerStyle,
      mode,
      testId,
      accessibileRef = null,
    } = this.props;

    const dropzoneClasses = classNames(classes.dropZone, dropzoneClass);
    const folderUploadSupportProps = isFolderPicker
      ? {
          getDataTransferItems: fromEvent,
        }
      : {};
    const maxDropSize = isMaxSizeArray
      ? undefined
      : _.max(maxSize, maxSizeForVideoFiles, maxSizeForZipFiles);

    return (
      <div className={classes.dropzoneContainer} style={dropzoneContainerStyle}>
        <Dropzone
          onDragEnter={this.handleOnDragEnter}
          onDragLeave={this.handleOnDragLeave}
          accept={this.props.accept}
          onDrop={this.handleOnDrop}
          className={dropzoneClasses}
          multiple={multiple}
          style={dropZoneStyle}
          disabled={mode === "view"}
          disableClick={mode === "view" || isDialogueBoxEnabled || disableClick}
          onFileDialogCancel={this.onClose}
          ref={ref => (this.dropZoneRef = ref)}
          maxSize={maxDropSize}
          onDropRejected={this.onDropRejected}
          inputProps={this.getDropzoneInputProps()}
          data-test-id={`${testId}-dropzone`}
          {...folderUploadSupportProps}
        >
          {this.props.renderComponent ? (
            <div
              className={classes.renderCompChild}
              onClick={() =>
                isDialogueBoxEnabled
                  ? this.toggleDialogueBoxDisplay()
                  : !_.isNil(accessibileRef)
                  ? _.noop
                  : null
              }
              ref={accessibileRef}
            >
              {this.props.renderComponent}
            </div>
          ) : null}
          {this.renderDragNDropAnimation()}
        </Dropzone>
        {showLockScreen && (
          <LockedScreenWrapper
            featureKey={`FILE_SIZE_LIMIT`}
            screenType={"modal"}
            onClose={this.onCloseLockScreenModal}
          ></LockedScreenWrapper>
        )}
        {showConfirmationDialogue && isDialogueBoxEnabled && (
          <Modal
            width="480px"
            height="fit-content"
            onClose={this.toggleDialogueBoxDisplay}
            dsVersion="2.0"
          >
            <Modal.Header title={dialogueBoxInfo.title} />
            <Modal.Body>{dialogueBoxInfo.message()}</Modal.Body>
            <Modal.Footer>
              <div className={classes.modalFooter}>
                <Button
                  variant="neutral"
                  type="outlined"
                  onClick={this.toggleDialogueBoxDisplay}
                  dsVersion="2.0"
                  testId={`${testId}-cancel`}
                >
                  {_.get(dialogueBoxInfo, "cancelButtonText", "")}
                </Button>
                <Button
                  variant="primary"
                  type="fill"
                  onClick={this.dialogueBoxConfirm}
                  dsVersion="2.0"
                  testId={`${testId}-confirm`}
                >
                  {_.get(dialogueBoxInfo, "confirmButtonText", "")}
                </Button>
              </div>
            </Modal.Footer>
          </Modal>
        )}
        <div
          onClick={e => {
            // To prevent the click event from propagating to the parent element and causing file picker to open
            e.stopPropagation();
          }}
        >
          {currentDialogAction ? this.renderModalComponent() : null}
          {isLoading ? <FullScreenLoader /> : null}
        </div>
        {/* Below div is required to detect the end of the page  */}
        <div ref={this.dummyContainerRef} className={classes.hidden} />
      </div>
    );
  }
}

DropzoneComponent.propTypes = {
  onContentUploaded: PropTypes.func,
  updateIsUploading: PropTypes.func,
  renderComponent: PropTypes.any,
  multiple: PropTypes.bool,
  dropZoneStyle: PropTypes.object,
  uploadFile: PropTypes.func,
  updateFilesCount: PropTypes.func,
  onSingleContentUploaded: PropTypes.func,
  onDropContent: PropTypes.func,
  mode: PropTypes.string,
  customizedFileObject: PropTypes.func,
  customizedFile: PropTypes.func,
  parentId: PropTypes.string,
  parentType: PropTypes.string,
  shouldUpload: PropTypes.bool,
  onClick: PropTypes.func,
  onClose: PropTypes.func,
  isAuthenticated: PropTypes.bool,
  dialogueBox: PropTypes.bool,
  dialogueBoxInfo: PropTypes.object,
  dropzoneClass: PropTypes.string,
  visibleTo: PropTypes.arrayOf(PropTypes.string),
  excludeFileExtensionsList: PropTypes.array,
  checkFileNameValidation: PropTypes.func,
  customFilterFilesHandler: PropTypes.func,
  testId: PropTypes.string,
  disableDragNDrop: PropTypes.bool,
};

DropzoneComponent.defaultProps = {
  accept: [
    "",
    "image/jpeg",
    "image/png",
    "image/jpg",
    "video/mp4",
    "video/quicktime",
    "audio/mp3",
    "audio/mpeg",
    "audio/m4a",
    "audio/x-m4a",
    "audio/vnd.wav",
    "audio/wav",
    "audio/aac",
    "audio/x-wav",
    "audio/vnd.dlna.adts",
    "audio/wave",
    "text/csv",
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/msword",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "application/x-iwork-numbers-sffnumbers",
    "application/x-iwork-keynote-sffkey",
    "application/x-iwork-pages-sffpages",
    "application/epub+zip",
    "application/zip",
    "application/vnd.ms-powerpoint.presentation.macroenabled.12",
  ],
  multiple: false,
  dropZoneStyle: {},
  mode: "edit",
  parentType: "",
  parentId: null,
  shouldUpload: true,
  isAuthenticated: true,
  isDialogueBoxEnabled: false,
  dialogueBoxInfo: DIALOGS_INFO.CONFIRM,
  isFolderPicker: false,
  isFileNameValidation: false,
  shouldAllowConflictingLabels: true,
  excludeFileExtensionsList: [],
  shouldShowDragNDropOverlay: false,
  testId: "UIComponents-dropzoneComponent",
  disableDragNDrop: false,
};

const mapActionCreators = {
  uploadFile,
  uploadFileV2,
  uploadFilesV3,
  removeProgressElement,
  setToastMsg,
  heicToJpg,
  exceededSizeError,
  validateResourceLabels,
  updateResourceAttachments,
  removeFiles,
  unsupportedFileExtensionError,
};

const mapStateToProps = (state, ownProps) => {
  let { maxSize } = ownProps;
  const { isIncreasedUploadLimit = false } = ownProps;
  const isCurriculumProgramFree = getCurriculumProgramFreeStatus({ state });
  const allowInfiniteFileSize = ACLStore.can(
    "FeatureFlag:DropzoneInfiniteFileSize"
  );

  const maxSizeForVideoFiles = getMaxSizeForVideoFiles({
    maxSize,
    isCurriculumProgramFree,
    is500MBVideoLimit: is500MBVideoUploadLimitEnabled(),
    allowInfiniteFileSize,
  });

  const maxSizeForZipFiles = getMaxSizeForZipFiles({
    maxSize,
    isCurriculumProgramFree,
    allowInfiniteFileSize,
    isIncreasedUploadLimit,
  });

  maxSize = getMaxSize({
    maxSize,
    isCurriculumProgramFree,
    allowInfiniteFileSize,
  });

  const enableHEICConversion = ACLStore.can("FeatureFlag:EnableHEICConversion");

  let acceptFileType = ownProps.accept;

  if (isWindows && enableHEICConversion) {
    if (
      _.isArray(ownProps.accept) &&
      _.some(ownProps.accept, fileType => fileType === "image/heic")
    ) {
      acceptFileType = [...ownProps.accept, ""];
    } else if (_.includes(ownProps.accept, "image/heic")) {
      acceptFileType = `${ownProps.accept},`;
    }
  }

  const accept = (_.isArray(acceptFileType)
    ? acceptFileType
    : _.split(acceptFileType, ",")
  )
    .filter(item => {
      return _.includes(["image/heic", "image/heif"], item)
        ? enableHEICConversion
        : true;
    })
    .join(",");

  const isMaxSizeArray = _.isArray(maxSize);

  return {
    isCurriculumProgramFree: state.login.userInfo.isCurriculumProgramFree,
    maxSize,
    maxSizeForVideoFiles,
    maxSizeForZipFiles,
    isMaxSizeArray,
    accept,
  };
};
export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, mapActionCreators, null, {
    withRef: true,
  })
)(DropzoneComponent);
